// Dates are stored as "YYYY-MM-DD" and times as "HH:mm"; parsing them as
// local time keeps the invitation on the day the host actually picked.
const toDate = (date, time = "00:00") => {
  if (!date) return null;
  const value = new Date(`${date}T${time || "00:00"}`);
  return Number.isNaN(value.getTime()) ? null : value;
};

export const formatDate = (date, language, options) => {
  const value = toDate(date);
  if (!value) return "";
  return new Intl.DateTimeFormat(
    language,
    options || { weekday: "long", day: "numeric", month: "long", year: "numeric" }
  ).format(value);
};

export const formatShortDate = (date, language) =>
  formatDate(date, language, { day: "numeric", month: "short", year: "numeric" });

export const formatTime = (time, language) => {
  const value = toDate("2000-01-01", time);
  if (!time || !value) return "";
  return new Intl.DateTimeFormat(language, { hour: "numeric", minute: "2-digit" }).format(value);
};

export const formatDateTime = (iso, language) => {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return "";
  return new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(value);
};

export const isPast = (date) => {
  const value = toDate(date, "23:59");
  return Boolean(value) && value.getTime() < Date.now();
};

export const today = () => {
  const value = new Date();
  const pad = (number) => String(number).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

export const downloadFile = (filename, content, mime) => {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// A two-hour calendar entry guests can add from the invitation.
export const buildIcs = (event, url) => {
  const start = toDate(event.date, event.time || "12:00");
  if (!start) return "";
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const stamp = (value) => value.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const escape = (text) => (text || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Gatherly//Invitation//EN",
    "BEGIN:VEVENT",
    `UID:${event.id}@gatherly`,
    `DTSTAMP:${stamp(new Date())}`,
    `DTSTART:${stamp(start)}`,
    `DTEND:${stamp(end)}`,
    `SUMMARY:${escape(event.title)}`,
    `LOCATION:${escape([event.venue, event.address].filter(Boolean).join(", "))}`,
    `DESCRIPTION:${escape(url)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};

// Copies text, falling back to a hidden text field on browsers (or insecure
// origins) where the clipboard API isn't available. Resolves to true on success.
export const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch {
      copied = false;
    }
    field.remove();
    return copied;
  }
};

// Whole days from today until the date ("YYYY-MM-DD"); negative once past.
export const daysUntil = (date) => {
  const target = toDate(date);
  if (!target) return null;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - start.getTime()) / 86400000);
};
